/**
 * Methodology API Service
 *
 * TypeScript client for the methodology slate endpoint. The backend
 * (methodology_catalog.build_methodology_response) already composes the full
 * cited, posture-tagged, attestable slate (BI methods + coverage levers +
 * property methods); this client surfaces it to the accountant Methodology
 * Comparison view. Uses the shared `api` utility for auth + error handling.
 */

import { api } from '../utils/api';
import type { ElectableApiMethod, MethodologyResponse } from '../types/methodology';

const BASE = '/api/v1/methodology';

/**
 * Fetch the full methodology slate for a report.
 *
 * In-house only: the endpoint is org-scoped via the Clerk session
 * (get_current_user); the witness/external path cannot reach it.
 */
export async function getMethodology(
  reportId: string,
  profile?: string,
): Promise<MethodologyResponse> {
  const qs = profile ? `?profile=${encodeURIComponent(profile)}` : '';
  return api.get<MethodologyResponse>(`${BASE}/${reportId}${qs}`);
}

/**
 * Elect which BI-loss method headlines the sworn documents (Three-Method
 * Attestation Schedule). Electing the path is the attorney's act; the accountant
 * attests each method's computation separately. Persists `data.elected_bi_method`
 * on the draft; the sworn PoL / demand / court bundle then foot to the elected figure.
 * `method` is the un-prefixed key (`bottom_up` | `top_down` | `reconciled`; the two trend methods are named but the server answers 409 — they are not electable).
 */
export async function electMethod(
  reportId: string,
  method: ElectableApiMethod,
): Promise<{ report_id: string; elected_method: string; status: string }> {
  return api.post(`/api/v1/case-figures/${reportId}/elect-method`, { method });
}
